import {
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  suppliersApi,
  SupplierProductGroupDto,
} from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  parseOptionalLeadTime,
  validateOptionalLeadTime,
} from './leadTimeUtils';

interface FormData {
  sampleLeadTime: string;
  bulkLeadTime: string;
  shipmentLeadTime: string;
}

interface Props {
  supplierId: number;
  productGroup: SupplierProductGroupDto;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updated: SupplierProductGroupDto) => void;
}

export function EditProductGroupModal({
  supplierId,
  productGroup,
  isOpen,
  onClose,
  onUpdated,
}: Props) {
  const { t } = useTranslation();

  const { request: updateRequest, error } = useApiRequest(
    suppliersApi.productGroups.update,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      sampleLeadTime:
        productGroup.sampleLeadTime !== null
          ? String(productGroup.sampleLeadTime)
          : '',
      bulkLeadTime:
        productGroup.bulkLeadTime !== null
          ? String(productGroup.bulkLeadTime)
          : '',
      shipmentLeadTime:
        productGroup.shipmentLeadTime !== null
          ? String(productGroup.shipmentLeadTime)
          : '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        sampleLeadTime:
          productGroup.sampleLeadTime !== null
            ? String(productGroup.sampleLeadTime)
            : '',
        bulkLeadTime:
          productGroup.bulkLeadTime !== null
            ? String(productGroup.bulkLeadTime)
            : '',
        shipmentLeadTime:
          productGroup.shipmentLeadTime !== null
            ? String(productGroup.shipmentLeadTime)
            : '',
      });
    }
  }, [isOpen, productGroup, reset]);

  const onSubmit = async (data: FormData) => {
    await request(
      updateRequest,
      [
        supplierId,
        productGroup.productGroupId,
        {
          sampleLeadTime: parseOptionalLeadTime(data.sampleLeadTime),
          bulkLeadTime: parseOptionalLeadTime(data.bulkLeadTime),
          shipmentLeadTime: parseOptionalLeadTime(data.shipmentLeadTime),
        },
      ],
      (updated) => {
        onUpdated(updated);
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontWeight="bold">
          {t('general.editValue', { value: productGroup.value })}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Flex direction="column" gap={6} pb={2}>
              {error && <ErrorDetails error={error} />}
              <Flex direction="column" gap={3}>
                <TexasInputWrapper
                  label={t('suppliers.sampleLeadTime')}
                  rightAddon={t('inquiry.days')}
                  error={errors.sampleLeadTime}
                >
                  <Input
                    type="number"
                    variant="texas"
                    min={0}
                    {...register('sampleLeadTime', {
                      validate: (v) => validateOptionalLeadTime(v, t),
                    })}
                  />
                </TexasInputWrapper>
                <TexasInputWrapper
                  label={t('suppliers.bulkLeadTime')}
                  rightAddon={t('inquiry.days')}
                  error={errors.bulkLeadTime}
                >
                  <Input
                    type="number"
                    variant="texas"
                    min={0}
                    {...register('bulkLeadTime', {
                      validate: (v) => validateOptionalLeadTime(v, t),
                    })}
                  />
                </TexasInputWrapper>
                <TexasInputWrapper
                  label={t('suppliers.shipmentLeadTime')}
                  rightAddon={t('inquiry.days')}
                  error={errors.shipmentLeadTime}
                >
                  <Input
                    type="number"
                    variant="texas"
                    min={0}
                    {...register('shipmentLeadTime', {
                      validate: (v) => validateOptionalLeadTime(v, t),
                    })}
                  />
                </TexasInputWrapper>
              </Flex>
              <SubmitButton loading={isSubmitting}>
                {t('general.update')}
              </SubmitButton>
            </Flex>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
}
